import { useState, useEffect, useRef, useCallback } from "react";
import styles from "./complaints.module.css";
import { Typography, Dialog, DialogTitle, DialogContent, Snackbar, Slide, IconButton } from "@mui/material";
import { Home, X, ArrowLeft } from "react-feather";
import Helmet from "react-helmet";
import Footer from "../../components/Footer";
import { useAuth0 } from "@auth0/auth0-react";
import Loader from "../../components/Loader";
import ComplaintFilter from "./components/ComplaintFilter";
import ComplaintCard from "./components/ComplaintCard";
import ComplaintMobileCard from "./components/ComplaintMobileCard";
import AddComplaint from "./components/AddComplaint";
import axios from "axios";
import { TableLoader } from "../../components/LoaderC";
import { AddLogCallAPI } from "../../components/AddLogs";
import { toast } from 'react-smart-toaster';
import withCustomAuthenticationRequired from "../../auth/withCustomAuthenticationRequired";

const INITIAL_LIMIT = 20;
const CHUNK_SIZE = 12;

const withCategoryImage = (item) => {
  if (item?.category === 'Noise')
    return { ...item, image: '/static/img/noise.svg' };
  if (item?.category === 'Garbage Removal')
    return { ...item, image: '/static/img/garbage.svg' };
  return { ...item, image: '/static/img/other.svg' };
}

const Complaints = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [filterData, setFilterData] = useState({ searchKey: "", property: "", apartment: "", status: "", priority: "" });
  const [sortData, setSortData] = useState("");
  const [allData, setAllData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(filterData?.searchKey || "");
  const [reloadKey, setReloadKey] = useState(0);

  const sentinelRef = useRef(null);
  const mobileSentinelRef = useRef(null);
  const requestSeq = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  // Debounce search text input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filterData?.searchKey || "");
    }, 400);
    return () => clearTimeout(timer);
  }, [filterData?.searchKey]);

  const fetchComplaints = useCallback(async (currentOffset, replace) => {
    const seq = ++requestSeq.current;

    let activeIndicator = "loadingMore";
    if (replace && !hasLoadedOnceRef.current) {
      activeIndicator = "contentLoading";
      setContentLoading(true);
    } else if (replace) {
      activeIndicator = "refetching";
      setRefetching(true);
    } else {
      setLoadingMore(true);
    }
    setLoadMoreError(false);

    const limit = currentOffset === 0 ? INITIAL_LIMIT : CHUNK_SIZE;
    const pageNum = Math.floor(currentOffset / limit) + 1;

    let params = {
      offset: currentOffset,
      limit: limit,
      page: pageNum,
      per_page: limit,
    };

    if (debouncedSearch.trim() !== "") params.search = debouncedSearch.trim();
    if (filterData.property !== "" && filterData.property !== "all") params.property_id = filterData.property;
    if (filterData.apartment !== "" && filterData.apartment !== "all") params.apt_id = filterData.apartment;
    if (filterData.status !== "" && filterData.status !== "all") params.status = filterData.status;
    if (filterData.priority !== "" && filterData.priority !== "all") params.priority = filterData.priority;
    if (sortData) params.sort = sortData === 'erliest' ? 'earliest' : 'latest';

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      const response = await axios.get(process.env.REACT_APP_CASE_API_URL + "v1/complaints/paginated", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params,
      });

      if (seq !== requestSeq.current) return;

      const rawChunk = response.data?.data || [];
      const newChunk = rawChunk.map(withCategoryImage);
      const meta = response.data?.meta || response.data?.pagination || {};

      setAllData((prev) => (replace ? newChunk : [...prev, ...newChunk]));

      const nextOffset = meta?.next_offset ?? (currentOffset + rawChunk.length);
      setOffset(nextOffset);

      const total = meta?.total ?? response.data?.pagination?.total ?? 0;
      const hasMoreMeta = meta?.has_more ?? response.data?.pagination?.has_more;

      if (hasMoreMeta !== undefined && hasMoreMeta !== null) {
        setHasMore(Boolean(hasMoreMeta));
      } else {
        setHasMore(rawChunk.length > 0 && nextOffset < total);
      }

      setLoadError(false);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error('[Complaints] Failed to load complaint list', error);
      toast.error(error?.response?.data?.message || 'Unable to load complaints. Please try again.');

      if (replace) {
        setLoadError(true);
        setAllData([]);
        setHasMore(false);
      } else {
        setLoadMoreError(true);
      }
    } finally {
      if (seq === requestSeq.current) {
        if (activeIndicator === "contentLoading") setContentLoading(false);
        else if (activeIndicator === "refetching") setRefetching(false);
        else setLoadingMore(false);
      }
    }
  }, [getAccessTokenSilently, debouncedSearch, filterData.property, filterData.apartment, filterData.status, filterData.priority, sortData]);

  // Load first chunk when search, filters change or reload requested
  useEffect(() => {
    fetchComplaints(0, true);
  }, [fetchComplaints, reloadKey]);

  // Infinite scroll: load next chunk when sentinel enters viewport
  useEffect(() => {
    const desktopNode = sentinelRef.current;
    const mobileNode = mobileSentinelRef.current;
    if (!desktopNode && !mobileNode) return;

    const observer = new IntersectionObserver((entries) => {
      const isAnyIntersecting = entries.some((entry) => entry.isIntersecting);
      if (isAnyIntersecting && hasMore && !loadingMore && !contentLoading && !refetching && !loadMoreError) {
        fetchComplaints(offset, false);
      }
    }, { threshold: 0.1 });

    if (desktopNode) observer.observe(desktopNode);
    if (mobileNode) observer.observe(mobileNode);

    return () => observer.disconnect();
  }, [hasMore, loadingMore, contentLoading, refetching, loadMoreError, offset, fetchComplaints]);

  const changeCStatus = async (item, cVal) => {
    if (item?.current_status !== cVal) {
      setLoading(true);

      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios.put(process.env.REACT_APP_CASE_API_URL + 'v1/complaints/' + item?.id, { 'current_status': cVal }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }).then(response => {
        setLoading(false);
        setSnackbarMsg(response.data.message);
        setSnackbarOpen(true);

        setAllData(prev => {
          return prev?.map(i => {
            if (i?.id === item?.id)
              return { ...i, current_status: cVal };
            return i;
          })
        });

        // The item may no longer match the active status filter, so refresh the list
        if (filterData.status !== "" && filterData.status !== "all")
          setReloadKey(prev => prev + 1);

        let logData = {
          'title': 'Complaint status has been updated',
          'description': [
            'Name: ' + response.data.data.title,
            'Complaints number: ' + response.data.data.complaint_no,
            'Apartment: ' + response.data.data.property_name + ' - ' + response.data.data.apt_name,
            'Tenant: ' + response.data.data.tenant_name
          ]
        }
        AddLogCallAPI(logData, token);
      }).catch(error => {
        setLoading(false);
        if (typeof error.response !== 'undefined')
          toast.error(error.response.data.message);
      });
    }
  }

  const onSubmitLocal = () => {
    // Reload from the server so the new complaint lands on the list and the total stays accurate
    setReloadKey(prev => prev + 1);
  }

  return (<>
    {loading && <Loader />}
    <Helmet title="Complaints" />
    <div className={`${styles.pageTitleWrap}`}>
      <Typography display="inline" className={`${styles.pageTitle}`}>Complaints</Typography>
      <nav aria-label="breadcrumb">
        <ol className={`${styles.breadcrumb}`}>
          <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Cases</li>
          <li className={`${styles.breadcrumbItem} ${styles.active}`}>Complaints</li>
        </ol>
      </nav>
    </div>

    <div className={`${styles.BodyCon}`}>
      <div className={`${styles.mainCardDiv}`}>
        <div className={`${styles.mainCard}`}>

          {contentLoading && (<div className="Loader TableLoader">
            <TableLoader />
          </div>)}

          {!contentLoading && (<div className="control-pane">
            <div className={`${styles.Row}`}>

              <ComplaintFilter setAddModalOpen={setAddModalOpen.bind(this)} filterData={filterData} setFilterData={setFilterData.bind(this)} sortData={sortData} setSortData={setSortData.bind(this)} />

              {allData.length > 0 && <>
                <div className={refetching ? `${styles.NotesRow} ${styles.Refetching}` : `${styles.NotesRow}`}>
                  {allData?.map((item) => {
                    return <ComplaintCard key={item?.id} item={item} changeCStatus={changeCStatus.bind(this)} />;
                  })}

                  {/* Sentinel: entering viewport loads next chunk on Desktop */}
                  <div ref={sentinelRef} className={styles.ScrollSentinel} />
                </div>

                <div className={`${styles.ListMobileView}`}>
                  {allData?.map((item) => {
                    return <ComplaintMobileCard key={item?.id} item={item} changeCStatus={changeCStatus.bind(this)} />
                  })}

                  {/* Sentinel: entering viewport loads next chunk on Mobile */}
                  <div ref={mobileSentinelRef} className={styles.ScrollSentinel} />
                </div>

                {loadingMore && (
                  <div className={styles.gridScrollStatus}>
                    <div className={styles.scrollLoaderWrap}>
                      <div className={styles.scrollSpinner} />
                      <span>Loading more complaints...</span>
                    </div>
                  </div>
                )}

                {loadMoreError && (
                  <div className={styles.gridScrollStatus}>
                    <p>Couldn't load more complaints.</p>
                    <button type="button" className={styles.RetryBu} onClick={() => fetchComplaints(offset, false)}>Retry</button>
                  </div>
                )}
              </>}

              {allData.length === 0 && loadError && (<div className={`${styles.NoDataMain}`}>
                <p className={`${styles.NoDataText}`}>Something went wrong</p>
                <p className={`${styles.NoDataTextSub}`}>We couldn't load complaints. Please try again.</p>
                <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchComplaints(0, true)}>Retry</button>
              </div>)}

              {allData.length === 0 && !loadError && (<div className={`${styles.NoDataMain}`}>
                <div className={`${styles.NoDataIMG}`}>
                  <img src="/static/img/no-request.png" alt="No Data" />
                </div>
                <p className={`${styles.NoDataText}`}>All caught up!</p>
                <p className={`${styles.NoDataTextSub}`}>There are currently no complaints to show.</p>
              </div>)}

            </div>
          </div>)}

        </div>
      </div>
    </div>

    <Dialog
      open={addModalOpen}
      fullWidth
      maxWidth="sm"
      onClose={(e) => setAddModalOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm"
    >
      <DialogTitle id="alert-dialog-title">
        <button className={`${styles.BackAddBU}`} onClick={(e) => setAddModalOpen(false)}><ArrowLeft /></button>
        New Complaint
        <button onClick={(e) => setAddModalOpen(false)}><X /></button>
      </DialogTitle>
      <DialogContent className={`${styles.RequestBodyPop}`}>
        <AddComplaint setAddModalOpen={setAddModalOpen.bind(this)} tenant_id={0} setLoading={setLoading.bind(this)} setSnackbarMsg={setSnackbarMsg.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} onSubmitLocal={onSubmitLocal.bind(this)} complaintData={null} />
      </DialogContent>
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      onClose={(e) => setSnackbarOpen(false)}
      TransitionComponent={Slide}
      message={snackbarMsg}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={(e) => setSnackbarOpen(false)}
        >
          <X />
        </IconButton>
      }
    />

    <Footer />
  </>);
};

export default withCustomAuthenticationRequired(Complaints, {
  onRedirecting: () => <Loader />,
});